import { Container, Divider } from "@material-ui/core";
import { Grid } from "@mui/material";

import axios from "axios";
import {
  FormCard,
  FormLabel,
  ModalError,
  ModalSuccess,
  MultipleSelectInput,
  NumberInput,
  Page,
  PrimaryButton,
  SectionLabel,
  SelectInput,
  SkeletonComponent,
  TextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { arrayToCommaSeparatedString, getErrors } from "utils";

const FormTax = () => {
  const classes = globalStyles();
  const history = useHistory();
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [payload, setPayload] = useState({
    code: "",
    description: "",
    method: "addition",
    object: [],
    rate: 0,
    tax_bases: 0,
  });

  const handleChangePayload = (result, key) => {
    if (key === "object") {
      const isExist = payload?.object?.some(
        item => item?.value === result?.value
      );
      setPayload(currentState => ({
        ...currentState,
        object: isExist
          ? currentState?.object?.filter(item => item?.value !== result?.value)
          : [...currentState?.object, result],
      }));
    } else {
      setPayload(currentState => ({
        ...currentState,
        [key]: result,
      }));
    }
  };

  const getDetail = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/tax/detail/${id}`, {
        headers,
      });
      const { data } = res?.data;
      setPayload(state => ({
        ...state,
        code: data?.code,
        description: data?.description,
        method: data?.method,
        rate: data?.rate,
        tax_bases: data?.tax_bases,
        object: (data?.tax_objects || [])?.map(item => ({
          name: optionTaxObject?.find(({ value }) => value === item?.tax_object)
            ?.name,
          value: item?.tax_object,
        })),
      }));
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const handleSubmit = async () => {
    const method = id ? axios.put : axios.post;
    try {
      setLoadingPage(true);
      const newPayload = {
        ...payload,
        object: payload?.object?.map(item => item?.value),
      };
      await method(
        `${hardBaseUrl}/tax/${id ? `update/${id}` : "create"}`,
        newPayload,
        { headers }
      );
      ModalSuccess(
        `Successfully ${id ? "Saved Changes" : "Added Tax"}`
      ).then(() => history.goBack());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    if (id) {
      getDetail();
    }
  }, []);

  return (
    <Page className={classes.root} title={`${id ? "Edit" : "Add"} Tax`}>
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title={`${id ? "Edit" : "Add"} Tax`}
            breadcrumbData={breadcrumbData(id)}
            backButton
          />
          <Divider className={classes.divider} />
          <SectionLabel
            title="Tax Information"
            subTitle={`Fill this tax information to ${
              id ? "edit" : "add"
            } tax for Client`}
          />
          <FormCard>
            <Grid container spacing={2}>
              {MockFieldForm?.map(
                ({ title, key, type, options, placeholder }, index) => (
                  <Grid
                    item
                    key={index}
                    xs={12}
                    sm={key === "description" ? 12 : 6}
                  >
                    <FormLabel label={title} required={key !== "description"} />
                    {type === "select" ? (
                      <SelectInput
                        value={payload?.[key]}
                        options={options}
                        optionKey="value"
                        optionLabel="name"
                        onChange={e =>
                          handleChangePayload(e?.target?.value, key)
                        }
                        width="100%"
                      />
                    ) : type === "multiple" ? (
                      <MultipleSelectInput
                        options={options}
                        multiple
                        optionKey="value"
                        optionLabel="name"
                        value={payload?.[key] || []}
                        textValue={arrayToCommaSeparatedString(
                          payload?.[key],
                          "name"
                        )}
                        onChange={(_, newResult) =>
                          handleChangePayload(newResult, key)
                        }
                        placeholder={placeholder}
                        width="100%"
                      />
                    ) : type === "number" ? (
                      <NumberInput
                        value={payload?.[key]}
                        onChange={value =>
                          handleChangePayload(Number(value), key)
                        }
                        endAdornment="%"
                      />
                    ) : (
                      <TextInput
                        value={payload?.[key]}
                        onChange={e =>
                          handleChangePayload(e?.target?.value, key)
                        }
                        placeholder={placeholder}
                        multiline={key === "description"}
                        rows={key === "description" ? 4 : 1}
                      />
                    )}
                  </Grid>
                )
              )}
            </Grid>
          </FormCard>
          <Grid container justifyContent="right">
            <PrimaryButton
              label={id ? "Save Changes" : "Add Tax"}
              onClick={handleSubmit}
            />
          </Grid>
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = id => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Configuration",
    link: "/admin/konfigurasi/pajak",
  },
  {
    label: "Tax",
    link: "/admin/konfigurasi/pajak",
  },
  {
    label: `${id ? "Edit" : "Add"} Tax`,
    active: true,
  },
];
const optionTaxMethod = [
  {
    name: "Addition",
    value: "addition",
  },
  {
    name: "Deduction",
    value: "deduction",
  },
];
const optionTaxObject = [
  {
    name: "Sales",
    value: "sales",
  },
  {
    name: "Payment",
    value: "payment",
  },
  {
    name: "Composer Tax",
    value: "composer-tax",
  },
];
const MockFieldForm = [
  {
    title: "Code",
    key: "code",
    placeholder: "Tax Code",
  },
  {
    title: "Rate",
    key: "rate",
    type: "number",
  },
  {
    title: "Tax Bases",
    key: "tax_bases",
    type: "number",
  },
  {
    title: "Method",
    key: "method",
    type: "select",
    options: optionTaxMethod,
  },
  {
    title: "Object",
    key: "object",
    type: "multiple",
    options: optionTaxObject,
    placeholder: "Tax Object",
  },
  {
    title: "Description",
    key: "description",
    placeholder: "Tax Description",
  },
];

export default FormTax;
